import { useEffect, useMemo, useState } from "react";
import {
    CircularProgress,
    TextField,
    Autocomplete,
} from "@mui/material";

interface AutoCompleteProps<T extends Record<string, any>> {
    field: {
        value: any;
        onChange: (value: any) => void;
        onBlur?: () => void;
    };
    error?: boolean;
    helperText?: string;
    label?: string;
    options?: T[];
    idKey?: keyof T;
    nameKey?: keyof T;
    disabled?: boolean;
    required?: boolean;
    freeSolo?: boolean;
    multiple?: boolean;
    returnObject?: boolean;
    loading?: boolean;
    loadingText?: string;
    placeholder?: string;
    onChange?: (value: any) => void;
    textFieldProps?: any;
}

const AutoComplete = <T extends Record<string, any>>({
    field,
    error,
    helperText,
    label,
    options = [],
    idKey = "Id" as keyof T,
    nameKey = "Name" as keyof T,
    disabled = false,
    required = false,
    freeSolo = false,
    multiple = false,
    returnObject = false,
    loading = false,
    loadingText = "Loading...",
    placeholder,
    onChange,
}: AutoCompleteProps<T>) => {
    const [inputValue, setInputValue] = useState("");

    const selectedValue = useMemo(() => {
        if (multiple) {
            if (!Array.isArray(field.value)) return [];

            if (returnObject) {
                return field.value;
            }

            return field.value.map((value: any) => {
                const option = options.find((item) => String(item[idKey]) === String(value));
                return option ?? value;
            });
        }

        if (!field.value) return null;

        if (returnObject) return field.value;

        return (
            options.find((item) =>
                String(item[idKey]) === String(field.value) || String(item[nameKey]) === String(field.value)
            ) || null
        );
    }, [
        field.value,
        options,
        idKey,
        nameKey,
        multiple,
        returnObject,
    ]);

    useEffect(() => {
        if (selectedValue) {
            setInputValue(getOptionLabel(selectedValue));
        } else if (typeof field.value === "string") {
            setInputValue(field.value);
        } else {
            setInputValue("");
        }
    }, [field.value, selectedValue]);

    const handleChange = (_: any, value: any) => {
        if (multiple) {
            if (returnObject) {
                field.onChange(value);
                onChange?.(value);
            } else {
                const values = value.map((item: any) =>
                    typeof item === "string" ? item : item[idKey]
                );

                field.onChange(values);
                onChange?.(values);
            }
            return;
        }

        if (!value) {
            field.onChange("");
            onChange?.("");
            return;
        }

        if (typeof value === "string") {
            field.onChange(value);
            setInputValue(value);
            onChange?.(value);
            return;
        }

        const finalValue = returnObject ? value : value[idKey];

        field.onChange(finalValue);
        setInputValue(getOptionLabel(value));
        onChange?.(finalValue);
    };

    const handleInputChange = (_: any, value: string, reason: string) => {
        setInputValue(value);

        if (freeSolo && !multiple && reason === "input") {
            field.onChange(value);
        }
    };

    const getOptionLabel = (option: any) => {
        if (typeof option === "string") return option;
        return String(option?.[nameKey] ?? "");
    };

    const isOptionEqualToValue = (option: any, value: any) => {
        if (!value) return false;

        if (typeof value !== "object") {
            return String(option[idKey]) === String(value);
        }

        return String(option[idKey]) === String(value[idKey]);
    };

    return (
        <Autocomplete
            freeSolo={freeSolo}
            multiple={multiple}
            disabled={disabled}
            loading={loading}
            loadingText={loadingText}
            options={options}
            value={selectedValue}
            inputValue={inputValue}
            selectOnFocus
            clearOnBlur={false}
            handleHomeEndKeys
            disableClearable={required}
            noOptionsText="No matching records found"
            onChange={handleChange}
            onInputChange={handleInputChange}
            filterOptions={(options, state) => {
                const input = state.inputValue.toLowerCase();

                return options.filter((option) =>
                    String(option[nameKey])
                        .toLowerCase()
                        .includes(input)
                );
            }}
            getOptionLabel={getOptionLabel}
            isOptionEqualToValue={isOptionEqualToValue}
            renderInput={(params) => (
                <TextField
                    {...params}
                    variant="standard"
                    label={label}
                    placeholder={placeholder}
                    required={required}
                    error={error}
                    helperText={helperText}
                    onBlur={() => {
                        if (freeSolo && !multiple) {
                            field.onChange(inputValue);
                        }

                        field.onBlur?.();
                    }}
                    slotProps={{
                        input: {
                            ...params.InputProps,
                            endAdornment: (
                                <>
                                    {loading && <CircularProgress size={20} />}
                                    {params.InputProps.endAdornment}
                                </>
                            ),
                        },
                    }}
                />
            )}
        />
    );
};

export default AutoComplete;